import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '../hooks/useWebSocket';
import { aiApi, AI_WS_URL } from '../api';
import BotCharacter from '../components/BotCharacter';
import {
  card, RankBadge, PnlCell, INTERVAL_COLORS, MoodBadge, IntervalBadge, Sparkline, downloadJson,
} from '../components/ArenaUI';
import {
  LineChart, Line, Tooltip, ResponsiveContainer, XAxis, YAxis,
  CartesianGrid, ReferenceLine,
} from 'recharts';

function TradeCalendar({ trades, color = '#38bdf8' }) {
  const closed = (trades || []).filter(t => t.direction === 'close' && t.closed_at);
  if (!closed.length) return null;
  const byDay = {};
  closed.forEach(t => {
    const day = t.closed_at.slice(0, 10);
    byDay[day] = Math.round(((byDay[day] || 0) + (t.pnl || 0)) * 100) / 100;
  });
  const days = Object.keys(byDay).sort();
  const months = [...new Set(days.map(d => d.slice(0, 7)))];
  const maxAbs = Math.max(...Object.values(byDay).map(Math.abs), 1);
  const monthNames = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>Calendário de Resultados</div>
      {months.slice(-3).map(month => {
        const [yr, mo] = month.split('-').map(Number);
        const firstDay = new Date(yr, mo - 1, 1).getDay();
        const daysInMonth = new Date(yr, mo, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
          const key = `${month}-${String(d).padStart(2, '0')}`;
          cells.push({ day: d, pnl: byDay[key] ?? null });
        }
        const monthPnl = days.filter(d => d.startsWith(month)).reduce((s, d) => s + byDay[d], 0);
        return (
          <div key={month} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{monthNames[mo-1]} {yr}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: monthPnl >= 0 ? '#34d399' : '#f87171' }}>
                {monthPnl >= 0 ? '+' : ''}${monthPnl.toFixed(2)}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
              {['D','S','T','Q','Q','S','S'].map((d, i) => (
                <div key={i} style={{ fontSize: 9, color: '#475569', textAlign: 'center', paddingBottom: 3 }}>{d}</div>
              ))}
              {cells.map((cell, i) => {
                if (!cell) return <div key={i} />;
                const { day, pnl } = cell;
                const hasTrade = pnl !== null;
                const intensity = hasTrade ? Math.min(Math.abs(pnl) / maxAbs, 1) : 0;
                const bg = !hasTrade ? '#1e293b'
                  : pnl > 0 ? `rgba(52,211,153,${0.2 + intensity * 0.65})`
                  : `rgba(248,113,113,${0.2 + intensity * 0.65})`;
                const label = hasTrade ? `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}` : '';
                return (
                  <div key={i} title={label} style={{
                    background: bg, borderRadius: 4, padding: '4px 2px', textAlign: 'center',
                    border: hasTrade ? `1px solid ${pnl > 0 ? '#34d39944' : '#f8717144'}` : '1px solid #1e293b',
                    cursor: hasTrade ? 'default' : 'default',
                  }}>
                    <div style={{ fontSize: 10, color: hasTrade ? '#0f172a' : '#475569', fontWeight: hasTrade ? 700 : 400 }}>{day}</div>
                    {hasTrade && (
                      <div style={{ fontSize: 9, color: '#0f172a', fontWeight: 800, lineHeight: 1.3 }}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
const GROUPS  = ['conservative', 'moderate', 'aggressive'];
const GROUP_LABELS = { conservative: '🛡️ Conservador', moderate: '⚖️ Moderado', aggressive: '⚡ Agressivo' };

const ALL_PROFILES = [
  { id: 'conservative', name: 'Conservador', emoji: '🛡️' },
  { id: 'moderate', name: 'Moderado', emoji: '⚖️' },
  { id: 'aggressive', name: 'Agressivo', emoji: '⚡' },
];
const ALL_TFS = ['1m', '5m', '15m', '1h', '4h', '1d'];

// Configuração avançada (Fase 5): lembra a última seleção do usuário como um
// "preset" implícito, sem precisar de tela de gerenciar presets nomeados.
function loadArenaConfig() {
  try {
    const raw = localStorage.getItem('arenaAdvancedConfig');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.profiles) && Array.isArray(parsed.timeframes)) return parsed;
  } catch {}
  return null;
}

export default function Competition() {
  const qc = useQueryClient();
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [arenaSymbol, setArenaSymbol] = useState('BTCUSDT');
  const [arenaCapital, setArenaCapital] = useState(5000);
  const savedConfig = loadArenaConfig();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState(savedConfig?.profiles || ALL_PROFILES.map(p => p.id));
  const [selectedTfs, setSelectedTfs] = useState(savedConfig?.timeframes || ALL_TFS);
  const [expandedBot, setExpandedBot] = useState(null);
  const [expandedPortfolio, setExpandedPortfolio] = useState(null);
  const [expandedStrategyCal, setExpandedStrategyCal] = useState(null);
  const [groupBy, setGroupBy] = useState('portfolio');
  const { data: tick } = useWebSocket(`${AI_WS_URL}/ws/prices/${symbol}`);

  const { data: arena } = useQuery({
    queryKey: ['arena'],
    queryFn: () => aiApi.get('/arena').then(r => r.data),
    refetchInterval: 5_000,
  });

  const { data: portfolios } = useQuery({
    queryKey: ['arena-portfolios'],
    queryFn: () => aiApi.get('/arena/portfolios').then(r => r.data),
    refetchInterval: 5_000,
    enabled: arena?.running,
  });

  const { data: comp } = useQuery({
    queryKey: ['competition'],
    queryFn: () => aiApi.get('/competition').then(r => r.data),
    refetchInterval: 5_000,
  });

  const { data: modelsInfo } = useQuery({
    queryKey: ['models'],
    queryFn: () => aiApi.get('/models').then(r => r.data),
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['arena'] });
    qc.invalidateQueries({ queryKey: ['competition'] });
    qc.invalidateQueries({ queryKey: ['bot-status'] });
    qc.invalidateQueries({ queryKey: ['models'] });
  };

  const [emergencyConfirm, setEmergencyConfirm] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const { data: arenaTrades } = useQuery({
    queryKey: ['arena-trades'],
    queryFn: () => aiApi.get('/arena/trades').then(r => r.data),
    enabled: showReport,
  });

  const toggleProfile = (id) => setSelectedProfiles(prev =>
    prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  const toggleTf = (tf) => setSelectedTfs(prev =>
    prev.includes(tf) ? prev.filter(t => t !== tf) : [...prev, tf]);

  const startArena = useMutation({
    mutationFn: () => {
      // Lembra a seleção pra próxima vez que abrir a tela (preset implícito)
      localStorage.setItem('arenaAdvancedConfig', JSON.stringify({
        profiles: selectedProfiles, timeframes: selectedTfs,
      }));
      return aiApi.post('/arena/start', {
        symbol: arenaSymbol, capital: arenaCapital,
        profiles: selectedProfiles, timeframes: selectedTfs,
      });
    },
    onSuccess: invalidate,
  });
  const stopArena = useMutation({ mutationFn: () => aiApi.post('/arena/stop'), onSuccess: invalidate });
  const resetArena = useMutation({ mutationFn: () => aiApi.post('/arena/reset'), onSuccess: invalidate });
  const forceTick = useMutation({ mutationFn: () => aiApi.post('/arena/tick'), onSuccess: invalidate });
  const emergencyClose = useMutation({
    mutationFn: () => aiApi.post('/arena/emergency-close'),
    onSuccess: () => { invalidate(); setEmergencyConfirm(false); },
  });
  const retrainFeedback = useMutation({
    mutationFn: () => aiApi.post(`/feedback/retrain/${symbol}?interval=4h&days=365`),
    onSuccess: invalidate,
  });

  // Train a specific interval model
  const trainModel = useMutation({
    mutationFn: (interval) => aiApi.post(`/train/${symbol}?interval=${interval}&days=365`),
    onSuccess: invalidate,
  });

  const bots = arena?.bots || [];
  const userSnap = comp?.user;

  // Check which intervals have trained models
  const trainedIntervals = new Set(
    (modelsInfo?.per_timeframe || []).filter(m => m.trained).map(m => m.interval)
  );

  // Needed intervals from active strategies
  const neededIntervals = [...new Set(bots.map(b => b.interval).filter(Boolean))];
  const missingModels = neededIntervals.filter(iv => !trainedIntervals.has(iv));

  // Combined leaderboard
  const allPlayers = [
    userSnap && {
      id: 'user', name: 'Você', emoji: '🧑', color: '#38bdf8',
      pnl_pct: userSnap.pnl_pct, pnl: userSnap.pnl,
      total_value: userSnap.total_value, win_rate: userSnap.win_rate,
      total_trades: userSnap.total_trades,
    },
    ...bots,
    // Fase 0: benchmark hold-BTC corre a mesma corrida
    ...(arena?.benchmark?.[arena.symbol] ? [{
      id: 'hold', name: `Hold ${arena.symbol.replace('USDT', '')}`, emoji: '🏦', color: '#94a3b8',
      pnl_pct: arena.benchmark[arena.symbol].pnl_pct,
      pnl: arena.benchmark[arena.symbol].value - (bots[0]?.initial_capital || 0),
      total_value: arena.benchmark[arena.symbol].value,
      isBenchmark: true,
    }] : []),
  ].filter(Boolean).sort((a, b) => b.pnl_pct - a.pnl_pct);

  // Arena aggregate stats
  const botsClosed = bots.flatMap(b => (b.recent_trades || []).filter(t => t.direction === 'close'));
  const arenaWins = botsClosed.filter(t => (t.pnl || 0) > 0).length;
  const arenaWinRate = botsClosed.length > 0 ? Math.round(arenaWins / botsClosed.length * 100) : 0;
  const totalArenaTradesCount = bots.reduce((s, b) => s + (b.total_trades || 0), 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Competição de Bots</h1>
        {arena && (
          <span style={{
            background: arena.running ? '#052e16' : '#1e293b',
            color: arena.running ? '#34d399' : '#64748b',
            borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700,
          }}>{arena.running ? '● ARENA AO VIVO' : '○ PARADO'}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Capital input */}
          {!arena?.running && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>Capital/portfólio:</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>$</span>
              <input
                type="number" min={100} step={500} value={arenaCapital}
                onChange={e => setArenaCapital(Math.max(100, Number(e.target.value)))}
                style={{ width: 80, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
                  borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
              />
              <span style={{ fontSize: 10, color: '#475569' }}>÷ {selectedTfs.length || 1} TFs = ${Math.round(arenaCapital/(selectedTfs.length || 1)).toLocaleString()}/TF</span>
            </div>
          )}

          {/* Symbol selector — only active when arena is stopped */}
          {!arena?.running && (
            <div style={{ display: 'flex', gap: 4 }}>
              {SYMBOLS.map(s => (
                <button key={s} onClick={() => setArenaSymbol(s)} style={{
                  background: arenaSymbol === s ? '#1e3a5f' : '#1e293b',
                  color: arenaSymbol === s ? '#38bdf8' : '#64748b',
                  border: `1px solid ${arenaSymbol === s ? '#38bdf8' : '#334155'}`,
                  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}>{s.replace('USDT','')}</button>
              ))}
            </div>
          )}
          {arena?.running && (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {arena.symbol} · {arena.bots?.length} bots
            </span>
          )}
          {!arena?.running && (
            <button onClick={() => setAdvancedOpen(o => !o)} style={{
              background: advancedOpen ? '#1e3a5f' : '#1e293b',
              color: advancedOpen ? '#38bdf8' : '#64748b',
              border: `1px solid ${advancedOpen ? '#38bdf8' : '#334155'}`,
              borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>⚙ Avançado {advancedOpen ? '▲' : '▼'}</button>
          )}
          {bots.length > 0 && (
            <button onClick={() => setShowReport(o => !o)} style={{
              background: showReport ? '#1e3a5f' : '#1e293b',
              color: showReport ? '#38bdf8' : '#64748b',
              border: `1px solid ${showReport ? '#38bdf8' : '#334155'}`,
              borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>📊 Relatório</button>
          )}
          <button onClick={() => resetArena.mutate()} style={{ background: '#1e293b', color: '#64748b', border: '1px solid #334155', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}>Reset</button>
          <button onClick={() => forceTick.mutate()} disabled={forceTick.isPending || !arena?.running} style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}>
            {forceTick.isPending ? '...' : '⚡ Tick'}
          </button>
          {arena?.running
            ? <button onClick={() => stopArena.mutate()} style={{ background: '#2d1515', color: '#f87171', border: '1px solid #f87171', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>⏹ Parar</button>
            : <button onClick={() => startArena.mutate()}
                disabled={startArena.isPending || !selectedProfiles.length || !selectedTfs.length}
                style={{ background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                {startArena.isPending ? 'Iniciando...' : `▶ Iniciar ${arenaSymbol.replace('USDT','')} (${selectedProfiles.length * selectedTfs.length} bots)`}
              </button>
          }
          {/* Emergency kill-switch */}
          {arena?.running && (
            emergencyConfirm
              ? <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#fbbf24' }}>Fechar tudo?</span>
                  <button onClick={() => emergencyClose.mutate()} disabled={emergencyClose.isPending}
                    style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #ef4444', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                    {emergencyClose.isPending ? '...' : 'CONFIRMAR'}
                  </button>
                  <button onClick={() => setEmergencyConfirm(false)}
                    style={{ background: '#1e293b', color: '#64748b', border: '1px solid #334155', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 11 }}>
                    Cancelar
                  </button>
                </div>
              : <button onClick={() => setEmergencyConfirm(true)}
                  title="Fecha todas as posições abertas imediatamente"
                  style={{ background: '#1c0a0a', color: '#ef4444', border: '1px solid #7f1d1d', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  🚨 Emergência
                </button>
          )}
        </div>
      </div>

      {/* Configuração avançada — quais perfis/timeframes entram na competição */}
      {!arena?.running && advancedOpen && (
        <div style={{ ...card, padding: '16px 20px', marginBottom: 16, marginTop: -12 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
            Escolha quais perfis e timeframes entram na competição — a última escolha fica salva pra próxima vez.
          </div>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 700 }}>PERFIS DE RISCO</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {ALL_PROFILES.map(p => {
                  const on = selectedProfiles.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => toggleProfile(p.id)} style={{
                      background: on ? '#1e3a5f' : '#0f172a',
                      color: on ? '#38bdf8' : '#64748b',
                      border: `1px solid ${on ? '#38bdf8' : '#334155'}`,
                      borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    }}>{p.emoji} {p.name}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 700 }}>TIMEFRAMES</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {ALL_TFS.map(tf => {
                  const on = selectedTfs.includes(tf);
                  const color = INTERVAL_COLORS[tf] || '#94a3b8';
                  return (
                    <button key={tf} onClick={() => toggleTf(tf)} style={{
                      background: on ? `${color}22` : '#0f172a',
                      color: on ? color : '#64748b',
                      border: `1px solid ${on ? color : '#334155'}`,
                      borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    }}>{tf}</button>
                  );
                })}
              </div>
            </div>
          </div>
          {(!selectedProfiles.length || !selectedTfs.length) && (
            <div style={{ fontSize: 11, color: '#f87171', marginTop: 10 }}>
              Selecione pelo menos 1 perfil e 1 timeframe pra poder iniciar.
            </div>
          )}
        </div>
      )}

      {/* Relatório da Arena — resumo da rodada atual antes de parar/reconfigurar */}
      {showReport && (
        <div style={{ ...card, padding: '20px', marginBottom: 16, border: '1px solid #38bdf8' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>📊 Relatório da Arena</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {arena?.symbol} · {arena?.running ? 'ao vivo' : 'parada'} · iniciada em{' '}
                {arena?.started_at ? new Date(arena.started_at).toLocaleString('pt-BR') : '—'} · gerado agora
              </div>
            </div>
            <button onClick={() => downloadJson(
              `arena-relatorio-${arena?.symbol || 'BTCUSDT'}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`,
              {
                generated_at: new Date().toISOString(),
                symbol: arena?.symbol, started_at: arena?.started_at, running: arena?.running,
                aggregate: { total_trades: totalArenaTradesCount, win_rate_pct: arenaWinRate },
                bots,
                trade_events: arenaTrades || [],
              }
            )} style={{ background: '#1e3a5f', color: '#38bdf8', border: '1px solid #38bdf8', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              ⬇ Baixar JSON
            </button>
          </div>

          <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 12, flexWrap: 'wrap' }}>
            <div><span style={{ color: '#64748b' }}>Trades totais </span><b>{totalArenaTradesCount}</b></div>
            <div><span style={{ color: '#64748b' }}>Win rate geral </span><b style={{ color: arenaWinRate >= 50 ? '#34d399' : '#f87171' }}>{arenaWinRate}%</b></div>
            {allPlayers[0] && (
              <div><span style={{ color: '#64748b' }}>Melhor </span><b>{allPlayers[0].emoji} {allPlayers[0].name}</b> <PnlCell value={allPlayers[0].pnl_pct} /></div>
            )}
            {allPlayers.length > 1 && (
              <div><span style={{ color: '#64748b' }}>Pior </span><b>{allPlayers[allPlayers.length - 1].emoji} {allPlayers[allPlayers.length - 1].name}</b> <PnlCell value={allPlayers[allPlayers.length - 1].pnl_pct} /></div>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: '#64748b', textAlign: 'left', borderBottom: '1px solid #334155' }}>
                  <th style={{ padding: '6px 8px' }}>#</th>
                  <th style={{ padding: '6px 8px' }}>Bot</th>
                  <th style={{ padding: '6px 8px' }}>TF</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>P&L</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Win%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Trades</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Drawdown</th>
                </tr>
              </thead>
              <tbody>
                {allPlayers.map((p, i) => (
                  <tr key={p.id} style={{
                    borderBottom: '1px solid #1e293b',
                    opacity: p.isBenchmark ? 0.75 : 1,
                    background: p.isBenchmark ? 'rgba(148,163,184,0.05)' : undefined,
                  }}>
                    <td style={{ padding: '6px 8px', color: '#64748b' }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {p.emoji} {p.name} {p.mood && <MoodBadge mood={p.mood} size={13} />}
                      {p.isBenchmark && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, padding: '1px 7px' }}>
                          benchmark — só comprou e segurou
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{p.interval || '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}><PnlCell value={p.pnl_pct} size={12} /></td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.win_rate_net != null ? p.win_rate_net.toFixed(0) : p.win_rate?.toFixed(0) ?? '—'}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.total_trades ?? 0}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#fca5a5' }}>
                      {p.max_drawdown_pct !== undefined ? `-${p.max_drawdown_pct.toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Live price + stats bar */}
      <div style={{ ...card, padding: '12px 20px', marginBottom: 16, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
        {tick && (
          <>
            <span style={{ color: '#64748b' }}>Live {symbol}</span>
            <b style={{ color: '#38bdf8', fontSize: 16 }}>${tick.price?.toLocaleString()}</b>
          </>
        )}
        {arena?.running && totalArenaTradesCount > 0 && (
          <>
            <span style={{ color: '#64748b' }}>Trades arena: <b style={{ color: '#e2e8f0' }}>{totalArenaTradesCount}</b></span>
            <span style={{ color: '#64748b' }}>Win rate geral: <b style={{ color: arenaWinRate >= 50 ? '#34d399' : '#f87171' }}>{arenaWinRate}%</b></span>
          </>
        )}
        {portfolios && (
          <span style={{ color: '#64748b' }}>
            Capital: <b style={{ color: '#e2e8f0' }}>${portfolios.capital_per_group?.toLocaleString()}</b>
            <span style={{ fontSize: 11, color: '#475569' }}> · ${portfolios.portfolios?.[0]?.capital_per_tf?.toFixed(0)}/TF</span>
          </span>
        )}
        {comp?.feedback_count > 0 && (
          <span style={{ marginLeft: 'auto', color: '#fbbf24', fontSize: 12 }}>
            💡 {comp.feedback_count} trades salvos
            {' '}<button onClick={() => retrainFeedback.mutate()} disabled={retrainFeedback.isPending}
              style={{ background: '#422006', color: '#fbbf24', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>
              {retrainFeedback.isPending ? 'Retreinando...' : 'Retreinar'}
            </button>
          </span>
        )}
      </div>

      {/* Missing models warning */}
      {missingModels.length > 0 && (
        <div style={{ ...card, background: '#1c1208', border: '1px solid #f59e0b', padding: '14px 20px', marginBottom: 16 }}>
          <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 10, fontSize: 13 }}>
            ⚠ Modelos não treinados — bots abaixo ficam inativos até treinar:
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {missingModels.map(iv => (
              <div key={iv} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IntervalBadge interval={iv} />
                <button
                  onClick={() => trainModel.mutate(iv)}
                  disabled={trainModel.isPending}
                  style={{ background: '#422006', color: '#fbbf24', border: '1px solid #f59e0b', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}
                >
                  {trainModel.isPending ? 'Treinando...' : `Treinar ${symbol} ${iv}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, color: '#94a3b8' }}>Placar · {bots.length} bots</h2>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {[['portfolio','Portfólio'],['strategy','Por Estratégia'],['tf','Por Timeframe'],['all','Todos']].map(([v,l]) => (
              <button key={v} onClick={() => setGroupBy(v)} style={{
                background: groupBy === v ? '#1e3a5f' : '#0f172a',
                color: groupBy === v ? '#38bdf8' : '#64748b',
                border: `1px solid ${groupBy === v ? '#38bdf8' : '#334155'}`,
                borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11,
              }}>{l}</button>
            ))}
          </div>
        </div>

        {allPlayers.length === 0 ? (
          <p style={{ color: '#64748b' }}>
            Selecione o papel, clique em Iniciar Arena e os 18 bots (3 estratégias × 6 TFs) vão começar a operar.
          </p>
        ) : groupBy === 'portfolio' ? (
          // 3 virtual portfolios — capital divided across 6 TFs per strategy
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(portfolios?.portfolios || []).map((p, i) => {
              const isOpen = expandedPortfolio === p.id;
              // Find per-TF bots with positions from leaderboard
              const tfBots = (arena?.bots || []).filter(b => b.id?.startsWith(p.id + '_'));
              const openPositions = tfBots.flatMap(b =>
                (b.positions || []).map(pos => ({ ...pos, interval: b.interval, botId: b.id }))
              );
              return (
                <div key={p.id} style={{ background: '#0f172a', borderRadius: 12, overflow: 'hidden', border: `1px solid ${p.color}33` }}>
                  {/* Collapsed header row */}
                  <div
                    onClick={() => setExpandedPortfolio(isOpen ? null : p.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                      cursor: 'pointer', background: `linear-gradient(90deg, ${p.color}11 0%, transparent 60%)`,
                      userSelect: 'none' }}>
                    <span style={{ fontSize: 20 }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                    <BotCharacter
                      seed={p.id}
                      mood={p.mood}
                      health={p.total_initial ? p.total_final / p.total_initial : 1}
                      totalTrades={p.total_trades}
                      hasPosition={openPositions.length > 0}
                      size={40}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: 15 }}>{p.emoji} {p.name}</span>
                        {openPositions.length > 0 && (
                          <span style={{ fontSize: 10, background: '#34d39922', color: '#34d399',
                            borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>
                            {openPositions.length} posição{openPositions.length > 1 ? 'ões' : ''} aberta{openPositions.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                        ${p.capital_per_tf?.toFixed(0)}/TF · melhor: <span style={{ color: INTERVAL_COLORS[p.best_tf] || '#94a3b8' }}>{p.best_tf}</span>
                        {' · '}{p.total_trades} trades · {p.win_rate}% win
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: (p.pnl_pct || 0) >= 0 ? '#34d399' : '#f87171' }}>
                        {(p.pnl_pct || 0) >= 0 ? '+' : ''}{p.pnl_pct?.toFixed(2)}%
                      </div>
                      <div style={{ fontSize: 11, color: '#475569' }}>
                        ${p.total_initial?.toLocaleString()} → ${p.total_final?.toLocaleString()}
                      </div>
                    </div>
                    <span style={{ color: '#334155', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>

                  {/* Expanded: TF breakdown + open positions */}
                  {isOpen && (
                    <div style={{ borderTop: '1px solid #1e293b' }}>
                      {/* TF grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', borderBottom: '1px solid #1e293b' }}>
                        {(p.tf_breakdown || []).map(tf => {
                          const tfColor = INTERVAL_COLORS[tf.interval] || '#94a3b8';
                          const sigColor = tf.signal === 'buy' ? '#34d399' : tf.signal === 'sell' ? '#f87171' : '#334155';
                          const tfBot = tfBots.find(b => b.interval === tf.interval);
                          const hasPos = (tfBot?.positions || []).length > 0;
                          return (
                            <div key={tf.interval} style={{ padding: '10px 8px', textAlign: 'center',
                              borderRight: '1px solid #1e293b',
                              background: hasPos ? '#052e1622' : 'transparent' }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: tfColor, marginBottom: 3 }}>{tf.interval}</div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: (tf.pnl_pct || 0) >= 0 ? '#34d399' : '#f87171' }}>
                                {(tf.pnl_pct || 0) >= 0 ? '+' : ''}{(tf.pnl_pct || 0).toFixed(1)}%
                              </div>
                              <div style={{ fontSize: 10, color: sigColor, fontWeight: 700, marginTop: 2 }}>
                                {tf.signal !== 'hold' && tf.confidence > 0 ? `${tf.signal === 'buy' ? '▲' : '▼'} ${((tf.confidence||0)*100).toFixed(0)}%` : '—'}
                              </div>
                              {hasPos && <div style={{ fontSize: 9, color: '#34d399', marginTop: 2 }}>● aberta</div>}
                            </div>
                          );
                        })}
                      </div>

                      {/* Open positions */}
                      {openPositions.length > 0 && (
                        <div style={{ padding: '10px 18px' }}>
                          <div style={{ fontSize: 10, color: '#475569', marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Posições Abertas
                          </div>
                          {openPositions.map((pos, j) => (
                            <div key={j} style={{ display: 'flex', gap: 12, fontSize: 11, padding: '5px 0',
                              borderBottom: '1px solid #1e293b', alignItems: 'center' }}>
                              <span style={{ color: INTERVAL_COLORS[pos.interval] || '#94a3b8', fontWeight: 700, minWidth: 30 }}>
                                {pos.interval}
                              </span>
                              <span style={{ color: pos.side === 'long' ? '#34d399' : '#f87171' }}>
                                {pos.side === 'long' ? '▲ LONG' : '▼ SHORT'}
                              </span>
                              <span style={{ color: '#64748b' }}>
                                entrada ${pos.entry_price?.toLocaleString()}
                              </span>
                              {pos.current_price && (
                                <span style={{ color: '#64748b' }}>
                                  atual ${pos.current_price?.toLocaleString()}
                                </span>
                              )}
                              {pos.pnl != null && (
                                <span style={{ color: (pos.pnl || 0) >= 0 ? '#34d399' : '#f87171', fontWeight: 700, marginLeft: 'auto' }}>
                                  {(pos.pnl || 0) >= 0 ? '+' : ''}${pos.pnl?.toFixed(2)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {openPositions.length === 0 && (
                        <div style={{ padding: '10px 18px', fontSize: 11, color: '#334155' }}>
                          Nenhuma posição aberta — aguardando sinal dos modelos.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {(!portfolios?.portfolios?.length) && (
              <p style={{ color: '#64748b' }}>
                Selecione o papel, defina o capital e clique em Iniciar Arena.
              </p>
            )}
          </div>
        ) : groupBy === 'strategy' ? (
          // Group by strategy, show TF performance matrix
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {GROUPS.map(grp => {
              const grpBots = bots.filter(b => b.id?.startsWith(grp + '_')).sort((a,b) => {
                const TF_ORDER = ['1m','5m','15m','1h','4h','1d'];
                return TF_ORDER.indexOf(a.interval) - TF_ORDER.indexOf(b.interval);
              });
              if (!grpBots.length) return null;
              const bestBot = [...grpBots].sort((a,b) => (b.pnl_pct||0) - (a.pnl_pct||0))[0];
              const groupTrades = grpBots.flatMap(b => b.recent_trades || []);
              const groupClosedCount = groupTrades.filter(t => t.direction === 'close').length;
              const isCalOpen = expandedStrategyCal === grp;
              const avgPnlPct = grpBots.reduce((s, b) => s + (b.pnl_pct || 0), 0) / grpBots.length;
              return (
                <div key={grp} style={{ background: '#0f172a', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid #1e293b' }}>
                    <BotCharacter
                      seed={grp}
                      mood={bestBot?.mood}
                      health={1 + avgPnlPct / 100}
                      totalTrades={groupTrades.filter(t => t.direction === 'close').length}
                      hasPosition={grpBots.some(b => (b.positions || []).length > 0)}
                      size={32}
                    />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{GROUP_LABELS[grp]}</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>melhor TF:</span>
                    <span style={{ fontSize: 12, color: INTERVAL_COLORS[bestBot?.interval] || '#94a3b8', fontWeight: 700 }}>
                      {bestBot?.interval}
                    </span>
                    <span style={{ fontSize: 13, color: (bestBot?.pnl_pct||0) >= 0 ? '#34d399' : '#f87171', fontWeight: 700, marginLeft: 4 }}>
                      {(bestBot?.pnl_pct||0) >= 0 ? '+' : ''}{bestBot?.pnl_pct?.toFixed(2)}%
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0 }}>
                    {grpBots.map(b => {
                      const sig = b.last_signal || {};
                      const pnl = b.pnl_pct || 0;
                      const tfColor = INTERVAL_COLORS[b.interval] || '#94a3b8';
                      return (
                        <div key={b.id} style={{ padding: '10px 12px', borderRight: '1px solid #1e293b', textAlign: 'center' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: tfColor, marginBottom: 4 }}>{b.interval}</div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: pnl >= 0 ? '#34d399' : '#f87171' }}>
                            {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}%
                          </div>
                          <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
                            {b.total_trades || 0}t · {b.win_rate || 0}%w
                          </div>
                          {sig.signal && sig.signal !== 'hold' && (
                            <div style={{ fontSize: 10, marginTop: 3, color: sig.signal === 'buy' ? '#34d399' : '#f87171', fontWeight: 700 }}>
                              {sig.signal === 'buy' ? '▲' : '▼'} {((sig.confidence||0)*100).toFixed(0)}%
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Calendário consolidado da estratégia (6 TFs combinados) */}
                  {groupClosedCount > 0 && (
                    <div style={{ borderTop: '1px solid #1e293b', padding: '8px 16px' }}>
                      <button
                        onClick={() => setExpandedStrategyCal(isCalOpen ? null : grp)}
                        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11, padding: 0 }}
                      >
                        {isCalOpen ? '▲ Ocultar calendário consolidado' : `▼ Ver calendário consolidado (${groupClosedCount} trades · 6 TFs)`}
                      </button>
                      {isCalOpen && (
                        <TradeCalendar trades={groupTrades} color={grpBots[0]?.color} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : groupBy === 'tf' ? (
          // Group by timeframe
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['1m','5m','15m','1h','4h','1d'].map(tf => {
              const tfBots = bots.filter(b => b.interval === tf).sort((a,b) => (b.pnl_pct||0) - (a.pnl_pct||0));
              if (!tfBots.length) return null;
              const tfColor = INTERVAL_COLORS[tf] || '#94a3b8';
              return (
                <div key={tf} style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: tfColor }}>{tf}</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>— {tfBots.length} bots</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {tfBots.map(b => (
                      <div key={b.id} style={{ background: '#1e293b', borderRadius: 6, padding: '6px 10px', minWidth: 100 }}>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.emoji} {GROUP_LABELS[b.group || b.id?.split('_')[0]]?.replace(/[^ ]+ /,'') || b.name}</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: (b.pnl_pct||0) >= 0 ? '#34d399' : '#f87171' }}>
                          {(b.pnl_pct||0) >= 0 ? '+' : ''}{(b.pnl_pct||0).toFixed(2)}%
                        </div>
                        <div style={{ fontSize: 10, color: '#475569' }}>{b.total_trades||0} trades</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allPlayers.map((player, i) => (
              <div key={player.id} style={{
                background: player.id === 'user' ? '#0c2a3d' : '#0f172a',
                border: `1px solid ${player.id === 'user' ? '#38bdf8' : '#334155'}`,
                borderRadius: 10, padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
              }}>
                <RankBadge rank={i + 1} />
                {player.id === 'user' ? (
                  <div style={{ fontSize: 22 }}>{player.emoji}</div>
                ) : (
                  <BotCharacter
                    seed={player.group || player.id?.split('_')[0]}
                    mood={player.mood}
                    health={player.initial_capital ? player.capital / player.initial_capital : 1}
                    totalTrades={player.total_trades}
                    hasPosition={(player.positions || []).length > 0}
                    size={36}
                  />
                )}
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{player.name}</span>
                    {player.interval && <IntervalBadge interval={player.interval} />}
                    {player.leverage && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>{player.leverage}x · {player.allow_short ? 'L+S' : 'Long'}</span>
                    )}
                  </div>
                  {player.last_signal?.signal && (
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      Sinal:{' '}
                      <span style={{
                        color: player.last_signal.signal === 'buy' ? '#34d399'
                          : player.last_signal.signal === 'sell' ? '#f87171' : '#fbbf24',
                        fontWeight: 700,
                      }}>
                        {player.last_signal.model === 'untrained' ? '— sem modelo' : player.last_signal.signal?.toUpperCase()}
                      </span>
                      {player.last_signal.confidence > 0 && ` (${Math.round(player.last_signal.confidence * 100)}%)`}
                    </div>
                  )}
                </div>
                <div style={{ width: 100 }}>
                  <Sparkline data={player.equity_history} color={player.color} initialValue={5000} />
                </div>
                <div style={{ textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Total</div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>${player.total_value?.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>P&L</div>
                  <PnlCell value={player.pnl_pct} />
                </div>
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Win%</div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{player.win_rate?.toFixed(0)}%</div>
                </div>
                <div style={{ textAlign: 'center', minWidth: 50 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Trades</div>
                  <div style={{ fontSize: 13 }}>{player.total_trades}</div>
                </div>
                {player.max_drawdown_pct !== undefined && (
                  <div style={{ textAlign: 'center', minWidth: 70 }}>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Drawdown</div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#fca5a5' }}>-{player.max_drawdown_pct?.toFixed(1)}%</div>
                  </div>
                )}
                {i === 0 && <span style={{ background: '#422006', color: '#fbbf24', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>LÍDER</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bot detail cards — only in 'all' mode to avoid clutter */}

      {/* Bot detail cards */}
      {bots.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {bots.map(b => {
            const isUntrained = b.last_signal?.model === 'untrained';
            const expanded = expandedBot === b.id;
            return (
              <div key={b.id} style={{
                ...card, borderTop: `3px solid ${b.color}`, marginBottom: 0,
                opacity: isUntrained ? 0.7 : 1,
              }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <BotCharacter
                      seed={b.group || b.id?.split('_')[0]}
                      mood={b.mood}
                      health={b.initial_capital ? b.capital / b.initial_capital : 1}
                      totalTrades={b.total_trades}
                      hasPosition={(b.positions || []).length > 0}
                      size={48}
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{b.emoji} {b.name}</span>
                        <IntervalBadge interval={b.interval} />
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {b.leverage}x · {b.allow_short ? 'Long+Short' : 'Long only'} · conf {Math.round((b.min_confidence || 0) * 100)}%
                      </div>
                    </div>
                  </div>
                  <PnlCell value={b.pnl_pct} size={16} />
                </div>

                {/* Untrained warning */}
                {isUntrained && (
                  <div style={{ background: '#1c1208', border: '1px solid #f59e0b', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#fbbf24', marginBottom: 8 }}>
                    ⚠ Modelo {b.interval} não treinado — bot inativo
                  </div>
                )}

                {/* Sparkline */}
                <Sparkline data={b.equity_history} color={b.color} initialValue={5000} />

                {/* Stats */}
                <div style={{ display: 'flex', gap: 16, marginTop: 6, marginBottom: 8, fontSize: 12 }}>
                  <div>
                    <span style={{ color: '#64748b' }}>Capital </span>
                    <b style={{ color: '#e2e8f0' }}>${b.capital?.toLocaleString()}</b>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Win </span>
                    <b style={{ color: '#e2e8f0' }}>{b.win_rate?.toFixed(0)}%</b>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Trades </span>
                    <b style={{ color: '#e2e8f0' }}>{b.total_trades}</b>
                  </div>
                  {b.max_drawdown_pct !== undefined && (
                    <div>
                      <span style={{ color: '#64748b' }}>Drawdown </span>
                      <b style={{ color: '#fca5a5' }}>-{b.max_drawdown_pct?.toFixed(1)}%</b>
                    </div>
                  )}
                  {b.unrealized_pnl !== 0 && (
                    <div>
                      <span style={{ color: '#64748b' }}>PnL aberto </span>
                      <b style={{ color: b.unrealized_pnl >= 0 ? '#34d399' : '#f87171' }}>
                        {b.unrealized_pnl >= 0 ? '+' : ''}${b.unrealized_pnl?.toFixed(2)}
                      </b>
                    </div>
                  )}
                </div>

                {/* Last signal + TA confirmation */}
                {b.last_signal?.signal && !isUntrained && (
                  <div style={{ fontSize: 11, marginBottom: 8, padding: '6px 10px', background: '#0f172a', borderRadius: 6 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ color: '#64748b' }}>ML:</span>
                      <span style={{
                        color: b.last_signal.signal === 'buy' ? '#34d399' : b.last_signal.signal === 'sell' ? '#f87171' : '#fbbf24',
                        fontWeight: 700,
                      }}>
                        {b.last_signal.signal?.toUpperCase()}
                      </span>
                      <span style={{ color: '#64748b' }}>{Math.round((b.last_signal.confidence || 0) * 100)}%</span>
                      <span style={{ color: '#475569' }}>·</span>
                      <span style={{ color: '#64748b' }}>Score TA:</span>
                      <span style={{
                        color: (b.last_signal.ta_score || 0) > 0 ? '#34d399' : (b.last_signal.ta_score || 0) < 0 ? '#f87171' : '#64748b',
                        fontWeight: 600,
                      }}>
                        {(b.last_signal.ta_score || 0) > 0 ? '+' : ''}{(b.last_signal.ta_score || 0).toFixed(1)}
                      </span>
                      {b.last_signal.signal !== 'hold' && (
                        <span style={{
                          background: b.last_signal.ta_confirmed ? '#052e16' : '#2d1515',
                          color: b.last_signal.ta_confirmed ? '#34d399' : '#f87171',
                          borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                        }}>
                          {b.last_signal.ta_confirmed ? '✓ confirmado' : '✗ bloqueado'}
                        </span>
                      )}
                      {b.last_tick_at && (
                        <span style={{ color: '#475569', marginLeft: 'auto' }}>
                          {new Date(b.last_tick_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Open positions */}
                {b.positions?.length > 0 ? (
                  <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                    {b.positions.map((p, i) => (
                      <div key={i} style={{ marginBottom: i < b.positions.length - 1 ? 6 : 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: p.side === 'long' ? '#34d399' : '#f87171', fontWeight: 700 }}>
                            {p.side === 'long' ? '▲ LONG' : '▼ SHORT'} {p.leverage}x
                          </span>
                          {p.pnl !== undefined && (
                            <span style={{ color: p.pnl >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>
                              {p.pnl >= 0 ? '+' : ''}${p.pnl?.toFixed(2)} ({p.pnl_pct?.toFixed(1)}%)
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
                          Entrada ${p.entry_price?.toLocaleString()} · Liq ${p.liquidation_price?.toLocaleString()} · Margem ${p.margin?.toFixed(0)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#475569', fontSize: 12, padding: '4px 0' }}>
                    {isUntrained ? '—' : 'Aguardando sinal...'}
                  </div>
                )}

                {/* Toggle recent trades */}
                {b.recent_trades?.filter(t => t.direction === 'close').length > 0 && (
                  <button
                    onClick={() => setExpandedBot(expanded ? null : b.id)}
                    style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11, marginTop: 8, padding: 0 }}
                  >
                    {expanded ? '▲ Ocultar trades' : `▼ Ver ${b.recent_trades.filter(t => t.direction === 'close').length} trades`}
                  </button>
                )}
                {expanded && (
                  <div style={{ marginTop: 8 }}>
                    {b.recent_trades.filter(t => t.direction === 'close').slice(-5).reverse().map((t, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '1px solid #1e293b' }}>
                        <span style={{ color: t.side === 'long' ? '#34d399' : '#f87171' }}>
                          {t.side === 'long' ? '▲' : '▼'} {t.side} {t.leverage}x
                        </span>
                        <span style={{ color: '#64748b' }}>{t.reason}</span>
                        <span style={{ color: (t.pnl || 0) >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>
                          {(t.pnl || 0) >= 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <TradeCalendar trades={b.recent_trades} color={b.color} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Instructions */}
      <div style={{ ...card, marginTop: 20, background: '#0f172a', border: '1px solid #334155' }}>
        <div style={{ fontSize: 12, color: '#94a3b8', background: '#1e3a5f33', border: '1px solid #38bdf855', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          💡 Esta é a Arena <b>legada</b> (18 bots, capital fatiado fixo por timeframe) — mantida porque o Backtest ainda depende dela. Pra pool único de capital de verdade, veja <b style={{ color: '#38bdf8' }}>🧠 Cérebros</b> no menu.
        </div>
        <h2 style={{ fontSize: 14, color: '#fbbf24', marginBottom: 10 }}>💡 Como funciona</h2>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 2 }}>
          <b style={{ color: '#e2e8f0' }}>18 bots</b> — 3 estratégias × 6 timeframes (1m, 5m, 15m, 1h, 4h, 1d)<br />
          <b style={{ color: '#e2e8f0' }}>Estratégias</b> — <span style={{ color: '#34d399' }}>Conservador</span> (1x alavancagem, long-only), <span style={{ color: '#fbbf24' }}>Moderado</span> (3x, L+S), <span style={{ color: '#f87171' }}>Agressivo</span> (8x, L+S)<br />
          <b style={{ color: '#e2e8f0' }}>Partial TP</b> — fecha 50% da posição ao atingir metade do TP, move SL para breakeven<br />
          <b style={{ color: '#e2e8f0' }}>Modelos</b> — cada bot usa o modelo do seu timeframe. Treine os modelos ausentes para ativar o bot.
        </div>
      </div>
    </div>
  );
}
